import {gmAPI, MapService, Markers} from '@/services/MapService';
import {Graphics} from '@/services/GraphicService';
import {AccessRouteGraphic, getAccessRouteMarkerIcon} from '@/services/GraphicService/AccessRouteGraphic/AccessRouteGraphic.js';
import {DisplayField} from '@/lib/classes/InfoWindowContent/DisplayField';

/** @typedef {import('./AccessRouteGraphic.js').AccessRouteGraphicData} AccessRouteGraphicData */

/**
 * Creates an AccessRouteGraphic from saved graphicData.
 * @param {AccessRouteGraphicData} data
 * @return {Promise<AccessRouteGraphic>}
 */
async function createAccessRouteGraphic(data) {
  /** @type {AccessRouteGraphic} */
  let graphic;
  try {
    graphic = new AccessRouteGraphic(data);
  } catch (err) {
    err.message = `Error creating AccessRouteGraphic: ${err.message}`;
    throw err;
  }
  try {
    await graphic.settingRoute;
  } catch (err) {
    err.message = `Error setting route data for AccessRouteGraphic: ${err.message}`;
    throw err;
  }
  return graphic;
}

/** @typedef {(graphicExport: import('../Graphic').GraphicExport) => void} AccessRouteCallback */

/**
 * Starts a map mode where the user can create a new access route by clicking to add route endpoints. Uses
 * AccessRouteGraphic to display a preview of the route.
 * @param {AccessRouteCallback} callback A callback to be invoked with the created access route data.
 * @return {function(): void} A function that cancels the route creation mode when invoked.
 */
function createNewAccessRoute(callback) {
  /** @type {google.maps.LatLngLiteral} */
  let origin;
  /** @type {google.maps.Marker} */
  let marker;
  // Unless the access route creation mode is cancelled early, the flow of events is:
  // START -> handleOriginClick -> handleDestinationClick -> exitCreateMode
  /**
   * Set the origin and listen for further events when the user clicks.
   * @param {google.maps.MapMouseEvent} event The click event.
   */
  const handleOriginClick = (event) => {
    origin = event.latLng.toJSON();
    // Create a map marker at the origin to mimic the map markers on the RouteRenderer.
    marker = createMapMarker(origin);
    MapService.addMapListener('createNewAccessRoute_click', 'click', handleDestinationClick);
  };
    /**
     * When the user clicks the destination, create a graphic to calculate the route.
     * @param {google.maps.MapMouseEvent} event The click event.
     */
  const handleDestinationClick = async (event) => {
    const graphic = new AccessRouteGraphic();
    await graphic.setRoute([origin, event.latLng.toJSON()]);
    let routeData;
    try {
      routeData = {
        ...graphic.toJSON(),
        name: 'Access Route',
        displayFields: [
          new DisplayField({
            label: 'Distance',
            unit: 'miles',
            value: graphic.distanceInMiles || undefined,
            allowEdit: false,
            allowLabelEdit: false,
            allowDelete: true,
          }),
          new DisplayField({
            label: 'Drive Time',
            unit: 'string',
            value: graphic.driveTime || undefined,
            allowEdit: false,
            allowLabelEdit: false,
            allowDelete: true,
          }),
        ],
      };
    } catch (err) {
      // Suppress NO_ROUTE errors.
      if (err.code !== 'NO_ROUTE') throw err;
    } finally {
      graphic.setMap(null);
      exitCreateMode();
      callback(routeData);
    }
  };
    /**
     * Remove all route creation event listeners and reset the map to its usual state.
     */
  const exitCreateMode = () => {
    MapService.setCursor(null);
    MapService.removeMapListener('createNewAccessRoute_click');
    Markers.setAllClickable(true);
    Graphics.setAllClickable(true);
    if (marker) {
      marker.setMap(null);
    }
    window.removeEventListener('keydown', escapeListener);
  };

  // Register initial listeners.
  MapService.addMapListener('createNewAccessRoute_click', 'click', handleOriginClick);
  const escapeListener = (event) => {
    if (event.key === 'Escape') {
      exitCreateMode();
    }
  };
  window.addEventListener('keydown', escapeListener, {once: true});

  // Set map settings.
  MapService.setCursor('crosshair');
  Markers.setAllClickable(false);
  Graphics.setAllClickable(false);

  return exitCreateMode;
}

/**
 * Helper to create a temp. map marker when creating the route.
 * @param {LatLngLiteral} position
 * @return {google.maps.Marker}
 */
function createMapMarker(position) {
  return new gmAPI.Marker({
    position,
    map: MapService.getMap(),
    draggable: false,
    icon: getAccessRouteMarkerIcon(),
  });
}

export {createAccessRouteGraphic, createNewAccessRoute};
